package top.wcpe.allincore.dsl.compiler.pipeline

import top.wcpe.allincore.dsl.compiler.ast.ClassDecl
import top.wcpe.allincore.dsl.compiler.ast.ComponentAst
import top.wcpe.allincore.dsl.compiler.ast.DrawAst
import top.wcpe.allincore.dsl.compiler.ast.DrawInstructionAst
import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.source.SourcePosition

/**
 * 组件 Props 校验器。
 *
 * 负责：
 * - 构建调用方 prop -> 表达式 的映射，并完成 DSL_E302/E309/E301 校验
 * - data 类型 prop 的 Map 字面量必填字段校验（DSL_E749）
 * - 组件 root 内 PropsRef 引用合法性校验（DSL_E306）
 */
internal class PropValidator(
    private val diag: DiagnosticCollector,
) {

    /**
     * 构建 props 映射表，同时完成 DSL_E302/E309/E301 校验。
     * 校验失败时仍继续收集其它错误；返回 null 表示存在阻塞性问题。
     *
     * @param dataDecls 当前 screen 的 data 声明索引，供 data 类型 prop 校验使用
     * @param universalProps 通用元素属性白名单，写在这些名字下的 prop 不视作未声明
     */
    fun buildPropMap(
        call: ElementAst,
        comp: ComponentAst,
        dataDecls: Map<String, ClassDecl>,
        universalProps: Set<String>,
    ): Map<String, ExprAst>? {
        val declaredNames = comp.props.map { it.name }.toSet()
        val callMap = call.props.associate { it.name to it }
        var hasError = false

        // DSL_E302：调用方传了组件未声明的 prop（通用元素属性除外，由 applyUniversalElementProps 处理）
        for (pa in call.props) {
            if (pa.name !in declaredNames && pa.name !in universalProps) {
                diag.error(
                    DslError.E302_UNKNOWN_PROP,
                    pa.position,
                    "组件 '${comp.id}' 未声明 prop '${pa.name}'",
                )
                hasError = true
            }
        }

        val propMap = mutableMapOf<String, ExprAst>()
        for (decl in comp.props) {
            val assign = callMap[decl.name]
            if (assign != null) {
                // DSL_E301：字面量类型校验
                checkLiteralType(assign.value, decl, comp.id, assign.position, dataDecls)
                propMap[decl.name] = assign.value
            } else if (decl.defaultValue != null) {
                propMap[decl.name] = decl.defaultValue
            } else {
                // DSL_E309：必传 prop 缺失
                diag.error(
                    DslError.E309_REQUIRED_PROP_MISSING,
                    call.position,
                    comp.id,
                    decl.name,
                )
                hasError = true
            }
        }

        return if (hasError) null else propMap
    }

    /** DSL_E306：校验组件 root 中所有 PropsRef 引用的 prop 是否已声明。 */
    fun validatePropsRefs(element: ElementAst, comp: ComponentAst) {
        val declaredNames = comp.props.map { it.name }.toSet()
        walkElementExprs(element) { expr ->
            if (expr is ExprAst.PropsRef && expr.name !in declaredNames) {
                diag.error(
                    DslError.E306_PROPS_REF_UNDECLARED,
                    expr.position,
                    comp.id,
                    expr.name,
                )
            }
        }
    }

    /** DSL_E301：校验调用方字面量类型与声明类型是否匹配。 */
    private fun checkLiteralType(
        value: ExprAst,
        decl: ComponentAst.PropDecl,
        compId: String,
        position: SourcePosition,
        dataDecls: Map<String, ClassDecl>,
    ) {
        // M6 review B：data 类型 prop 走独立校验路径
        if (decl.type == ComponentAst.PropType.DATA) {
            checkDataPropArg(value, decl, compId, position, dataDecls)
            return
        }
        val accepted = when (value) {
            is ExprAst.Literal.StringLit -> decl.type == ComponentAst.PropType.STRING || decl.type == ComponentAst.PropType.COLOR
            is ExprAst.Interp -> decl.type == ComponentAst.PropType.STRING || decl.type == ComponentAst.PropType.COLOR
            is ExprAst.Literal.NumberLit -> decl.type == ComponentAst.PropType.NUMBER
            is ExprAst.Literal.BoolLit -> decl.type == ComponentAst.PropType.BOOL
            is ExprAst.NullLit -> decl.type == ComponentAst.PropType.FUN
            is ExprAst.FunRefExpr -> decl.type == ComponentAst.PropType.FUN
            // 非字面量表达式跳过类型校验。
            // 注意 CallExpr（用户 fun 调用，如 `quality = rarityQuality(r)`）是运行期求值的【值】、
            // 不是方法引用：其返回类型编译期不可靠（fun 可不标返回类型），与内置访问器 MemberCall /
            // IdentRef 等运行期表达式同样跳过校验。曾把 CallExpr 限定为只配 FUN prop，导致「声明返回
            // String 的 fun 喂给 string prop」被误报 E301（FunRefExpr `::name` 才是方法引用，仍配 FUN prop）。
            else -> return
        }
        if (!accepted) {
            diag.error(
                DslError.E301_LITERAL_TYPE_MISMATCH,
                position,
                "组件 '$compId' 的 prop '${decl.name}' 声明为 ${decl.type.name.lowercase()}，传入的字面量类型不匹配",
            )
        }
    }

    /**
     * M6 review B：校验调用方传给 data 类型 prop 的实参表达式。
     *
     * 简化策略（与 checkLiteralType 对应）：
     * - MapLiteralExpr → 按目标 data 的字段表做必填字段校验（E749）
     * - 不兼容字面量（StringLit / NumberLit / BoolLit / NullLit / Interp / FunRefExpr）→ 报 E301
     * - 其它（IdentRef / MemberAccess / CallExpr 等运行时表达式）→ 跳过编译期校验
     *
     * 字段类型不匹配（E751）和未知字段（W730）由 SemanticChecker.DataLiteralChecker 阶段
     * 在展开后扫到 MapLiteralExpr 时统一负责，这里仅守住"必传字段缺失"这条最致命的红线。
     */
    private fun checkDataPropArg(
        value: ExprAst,
        decl: ComponentAst.PropDecl,
        compId: String,
        position: SourcePosition,
        dataDecls: Map<String, ClassDecl>,
    ) {
        if (value is ExprAst.MapLiteralExpr) {
            val dataDecl = decl.dataTypeName?.let { dataDecls[it] } ?: return
            checkMapLiteralRequiredFields(value, dataDecl)
            return
        }
        if (isIncompatibleLiteralForData(value)) {
            diag.error(
                DslError.E301_LITERAL_TYPE_MISMATCH,
                position,
                "组件 '$compId' 的 prop '${decl.name}' 声明为 data ${decl.dataTypeName ?: "?"}，" +
                    "传入的字面量类型不匹配（仅接受 #{...} Map 字面量或运行时表达式）",
            )
        }
        // 其它表达式（IdentRef / MemberAccess / CallExpr 等运行时表达式）跳过编译期校验
    }

    /** 判断 [value] 是否为已知不兼容 data 类型的字面量。 */
    private fun isIncompatibleLiteralForData(value: ExprAst): Boolean = when (value) {
        is ExprAst.Literal.StringLit, is ExprAst.Literal.NumberLit,
        is ExprAst.Literal.BoolLit, is ExprAst.NullLit,
        is ExprAst.Interp, is ExprAst.FunRefExpr -> true
        else -> false
    }

    /**
     * 仅做"必填字段缺失"校验（E749）。
     *
     * 字段类型不匹配 / 未知字段交给 SemanticChecker.DataLiteralChecker 统一处理，
     * 避免本类与该 Checker 的诊断逻辑重复。
     */
    private fun checkMapLiteralRequiredFields(
        map: ExprAst.MapLiteralExpr,
        dataDecl: ClassDecl,
    ) {
        val provided = map.entries.mapTo(linkedSetOf()) { it.key }
        for (field in dataDecl.fields) {
            if (field.default == null && field.name !in provided) {
                diag.error(
                    DslError.E749_DATA_REQUIRED_FIELD_MISSING,
                    map.position,
                    dataDecl.name, field.name,
                )
            }
        }
    }

    /** 遍历元素树中所有表达式，对每个 ExprAst 节点调用 visitor。 */
    private fun walkElementExprs(element: ElementAst, visitor: (ExprAst) -> Unit) {
        for (pa in element.props) walkExpr(pa.value, visitor)
        for (modifier in element.modifiers) {
            for (field in modifier.fields) walkExpr(field.value, visitor)
        }
        for (child in element.children) walkElementExprs(child, visitor)
        // 风险 4 遍历审计：与 children 对称下钻具名插槽填充内容，避免其中的 PropsRef 漏检。
        for (content in element.namedSlotContent.values) {
            for (child in content) walkElementExprs(child, visitor)
        }
        // 数据驱动容器的 sourceExpr（`source = props.suggestions`）也要参与 E306 校验，
        // 与 ExpressionSubstitutor / PropsRefLeakDetector 保持对称下钻。
        element.sourceExpr?.let { walkExpr(it, visitor) }
        // canvas 元素需下钻 attrs 与 draw/once 指令体内的表达式，
        // 否则 draw 块里 `arc(..., props.color)` 这类引用会被 validate / leak 扫描漏掉。
        element.canvasData?.let { canvas ->
            walkExpr(canvas.width, visitor)
            walkExpr(canvas.height, visitor)
            canvas.durationMs?.let { walkExpr(it, visitor) }
            for (attr in canvas.attrs) walkExpr(attr.value, visitor)
            walkDrawBlock(canvas.drawBlock, visitor)
            canvas.onceBlock?.let { walkDrawBlock(it, visitor) }
        }
    }

    /** 遍历 [DrawAst] 内每条指令上的表达式。 */
    private fun walkDrawBlock(drawAst: DrawAst, visitor: (ExprAst) -> Unit) {
        for (ins in drawAst.instructions) walkDrawInstruction(ins, visitor)
    }

    /** 遍历单条 draw 指令携带的所有表达式（含嵌套 for/if 体）。 */
    private fun walkDrawInstruction(ins: DrawInstructionAst, visitor: (ExprAst) -> Unit) {
        when (ins) {
            is DrawInstructionAst.Primitive -> ins.args.forEach { walkExpr(it, visitor) }
            is DrawInstructionAst.ValAssign -> walkExpr(ins.value, visitor)
            is DrawInstructionAst.For -> ins.body.forEach { walkDrawInstruction(it, visitor) }
            is DrawInstructionAst.IfElse -> {
                walkExpr(ins.condition, visitor)
                ins.thenBranch.forEach { walkDrawInstruction(it, visitor) }
                ins.elseBranch.forEach { walkDrawInstruction(it, visitor) }
            }
        }
    }

    /** 深度遍历 ExprAst 子树，对每个节点调用 visitor。 */
    private fun walkExpr(expr: ExprAst, visitor: (ExprAst) -> Unit) {
        visitor(expr)
        when (expr) {
            is ExprAst.BinaryOp -> { walkExpr(expr.left, visitor); walkExpr(expr.right, visitor) }
            is ExprAst.UnaryOp -> walkExpr(expr.operand, visitor)
            is ExprAst.MemberAccess -> walkExpr(expr.target, visitor)
            is ExprAst.SafeMemberAccess -> walkExpr(expr.target, visitor)
            // M10 Stage 6：`target!!` 透传 target
            is ExprAst.NonNullAssert -> walkExpr(expr.target, visitor)
            is ExprAst.MemberCall -> { walkExpr(expr.target, visitor); expr.args.forEach { walkExpr(it, visitor) }; expr.namedArgs.forEach { walkExpr(it.value, visitor) } }
            is ExprAst.SafeMemberCall -> { walkExpr(expr.target, visitor); expr.args.forEach { walkExpr(it, visitor) } }
            is ExprAst.Elvis -> { walkExpr(expr.left, visitor); walkExpr(expr.right, visitor) }
            is ExprAst.Ternary -> { walkExpr(expr.cond, visitor); walkExpr(expr.thenExpr, visitor); walkExpr(expr.elseExpr, visitor) }
            is ExprAst.Interp -> expr.parts.forEach { if (it is ExprAst.Interp.InterpPart.ExprPart) walkExpr(it.expr, visitor) }
            is ExprAst.ListLiteralExpr -> expr.elements.forEach { walkExpr(it, visitor) }
            is ExprAst.MapLiteralExpr -> expr.entries.forEach { walkExpr(it.value, visitor) }
            is ExprAst.RangeExpr -> { walkExpr(expr.start, visitor); walkExpr(expr.endValue, visitor) }
            is ExprAst.IndexExpr -> { walkExpr(expr.target, visitor); walkExpr(expr.index, visitor) }
            is ExprAst.CallExpr -> { walkExpr(expr.callee, visitor); expr.args.forEach { walkExpr(it, visitor) } }
            is ExprAst.BuiltinCallExpr -> expr.args.forEach { walkExpr(it, visitor) }
            is ExprAst.NetRequestCall -> {
                expr.payload.forEach { walkExpr(it.value, visitor) }
                expr.callback?.let { walkExpr(it, visitor) }
                expr.timeoutMs?.let { walkExpr(it, visitor) }
            }
            is ExprAst.NetSubscribeCall -> walkExpr(expr.handler, visitor)
            is ExprAst.AwaitExpr -> {
                expr.netRequestCall?.let { walkExpr(it, visitor) }
                expr.suspendCall?.let { walkExpr(it, visitor) }
                expr.tailExpr?.let { walkExpr(it, visitor) }
            }
            // D1：lambda 体内深度遍历语句子树，避免 PropsRef 漏检测。
            is ExprAst.LambdaExpr -> walkStmtList(expr.body, visitor)
            is ExprAst.IfExpr -> {
                walkExpr(expr.cond, visitor)
                walkStmtList(expr.thenBody, visitor)
                walkStmtList(expr.elseBody, visitor)
            }
            // D1：when 分支体语句也要深入。
            is ExprAst.WhenExpr -> {
                walkExpr(expr.subject, visitor)
                expr.branches.forEach { br ->
                    br.values.forEach { walkExpr(it, visitor) }
                    walkStmtList(br.body, visitor)
                }
                expr.elseBody?.let { walkStmtList(it, visitor) }
            }
            // 叶子节点无子表达式
            is ExprAst.Literal, is ExprAst.StateRef, is ExprAst.SessionRef, is ExprAst.PersistRef, is ExprAst.DerivedRef, is ExprAst.PropsRef,
            is ExprAst.IdentRef, is ExprAst.LocalRef,
            is ExprAst.ThemeRef, is ExprAst.NullLit, is ExprAst.FunRefExpr -> Unit
            // M5-c：IsExpr 仅下钻 target 子表达式
            is ExprAst.IsExpr -> walkExpr(expr.target, visitor)
        }
    }

    /**
     * 通用 helper：对 [stmts] 中每条语句的所有子表达式调用 [visitor]。
     *
     * 覆盖全部 StmtAst 子类型；递归进入控制流块（if/while/for/try/when）以及
     * 局部 fun / await 降级语句的子结构。
     */
    private fun walkStmtList(stmts: List<StmtAst>, visitor: (ExprAst) -> Unit) {
        for (s in stmts) walkStmt(s, visitor)
    }

    private fun walkStmt(s: StmtAst, visitor: (ExprAst) -> Unit) {
        when (s) {
            is StmtAst.ExprStmt -> walkExpr(s.call, visitor)
            is StmtAst.AssignStmt -> walkExpr(s.rhs, visitor)
            is StmtAst.VarDeclStmt -> walkExpr(s.initializer, visitor)
            is StmtAst.IfStmt -> walkIfStmt(s, visitor)
            is StmtAst.WhileStmt -> { walkExpr(s.cond, visitor); walkStmtList(s.body, visitor) }
            is StmtAst.ForInStmt -> { walkExpr(s.iterable, visitor); walkStmtList(s.body, visitor) }
            is StmtAst.WhenStmt -> walkExpr(s.whenExpr, visitor)
            is StmtAst.ReturnStmt -> s.value?.let { walkExpr(it, visitor) }
            is StmtAst.TryStmt -> walkTryStmt(s, visitor)
            // batch 语句：递归遍历 body 内子表达式（与 TryStmt 的 tryBody 处理一致）
            is StmtAst.BatchStmt -> walkStmtList(s.body, visitor)
            is StmtAst.FunDeclStmt -> walkStmtList(s.decl.body, visitor)
            is StmtAst.BreakStmt, is StmtAst.ContinueStmt -> Unit
        }
    }

    private fun walkIfStmt(s: StmtAst.IfStmt, visitor: (ExprAst) -> Unit) {
        walkExpr(s.cond, visitor)
        walkStmtList(s.thenBody, visitor)
        walkStmtList(s.elseBody, visitor)
    }

    private fun walkTryStmt(s: StmtAst.TryStmt, visitor: (ExprAst) -> Unit) {
        walkStmtList(s.tryBody, visitor)
        walkStmtList(s.catchBody, visitor)
        walkStmtList(s.finallyBody, visitor)
    }
}
